import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Printer,
  FileText,
  Sparkles,
  ChevronRight,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Loader2
} from 'lucide-react';

const LEVEL_LABELS = { P4: 'Primary 4', P5: 'Primary 5', P6: 'Primary 6' };

const WorksheetsPage = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState(null);
  const [past, setPast] = useState([]);
  const [pickedType, setPickedType] = useState(null);
  const [pickedLevel, setPickedLevel] = useState('P5');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, m] = await Promise.all([api.worksheetTopics(), api.worksheetMine()]);
        setTopics(t);
        setPast(m.worksheets || []);
        if (t.types?.length) setPickedType(t.types[0].key);
        // Lock level to the student's subscribed level (unless admin/teacher).
        if (t.user_level && !t.can_pick_any_level) {
          setPickedLevel(t.user_level);
        } else if (t.user_level) {
          setPickedLevel(t.user_level);
        }
      } catch (e) {
        toast.error('Could not load worksheet options');
      }
    })();
  }, []);

  const handleGenerate = async () => {
    if (!pickedType || !pickedLevel) return;
    setGenerating(true);
    const toastId = toast.loading('Generating 10 questions with AI… this takes ~15 seconds');
    try {
      const ws = await api.worksheetGenerate(pickedType, pickedLevel);
      toast.success('Worksheet ready!', { id: toastId });
      navigate(`/worksheets/${ws.id}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to generate worksheet';
      toast.error(typeof msg === 'string' ? msg : 'Failed to generate worksheet', { id: toastId });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1.5 text-slate-600 hover:text-emerald-700 font-semibold text-sm mb-6"
          data-testid="worksheets-back"
        >
          <ArrowLeft size={16} /> Back to dashboard
        </button>

        <div className="mb-8">
          <div className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1.5 rounded-full mb-3">
            <Printer size={12} /> New feature
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-slate-900 mb-2">Printable Worksheets</h1>
          <p className="text-slate-600 max-w-2xl">
            Generate a 10-question MCQ worksheet, print it on A4, let your child answer on the OCR bubble sheet, then snap a photo and upload — Prof.Scaly grades it automatically and updates their progress.
          </p>
        </div>

        {/* Generator card */}
        <Card className="p-6 md:p-8 rounded-3xl border-2 border-emerald-200 shadow-lg mb-10">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">1. Choose a topic</div>
              <div className="grid grid-cols-1 gap-2" data-testid="worksheets-type-list">
                {topics?.types?.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setPickedType(t.key)}
                    className={`text-left px-4 py-3 rounded-2xl border-2 transition-colors font-semibold ${
                      pickedType === t.key
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300'
                    }`}
                    data-testid={`worksheets-type-${t.key}`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{t.label}</span>
                      {pickedType === t.key && <CheckCircle2 size={18} className="text-emerald-600" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center justify-between">
                <span>2. Choose a level</span>
                {topics?.user_level && !topics?.can_pick_any_level && (
                  <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full normal-case tracking-normal">
                    Locked to {topics.user_level}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3" data-testid="worksheets-level-list">
                {topics?.levels?.map((l) => {
                  const locked = topics?.user_level && !topics?.can_pick_any_level && l !== topics.user_level;
                  return (
                    <button
                      key={l}
                      onClick={() => {
                        if (locked) {
                          toast.info(`You're subscribed to ${topics.user_level}. Worksheets can only be generated for your level.`);
                          return;
                        }
                        setPickedLevel(l);
                      }}
                      disabled={locked}
                      className={`px-4 py-3 rounded-2xl border-2 font-bold transition-colors ${
                        pickedLevel === l
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                          : locked
                          ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed opacity-60'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300'
                      }`}
                      data-testid={`worksheets-level-${l}`}
                      aria-disabled={locked}
                    >
                      {l}
                    </button>
                  );
                })}
              </div>
              {topics?.user_level && !topics?.can_pick_any_level && (
                <p className="text-[11px] text-slate-500 mb-6 leading-relaxed">
                  Worksheets are restricted to your subscribed level. To change it, update your profile.
                </p>
              )}

              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">3. Generate</div>
              <Button
                onClick={handleGenerate}
                disabled={generating || !pickedType || !pickedLevel}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-6 rounded-2xl text-base"
                data-testid="worksheets-generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} /> Generating 10 questions…
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2" size={18} /> Generate Worksheet
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                Each worksheet contains 10 fresh AI-generated questions for {pickedLevel && LEVEL_LABELS[pickedLevel]} — with a printable question paper, a bubble answer sheet, and an answer key.
              </p>
            </div>
          </div>
        </Card>

        {/* Past worksheets */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-heading font-bold text-slate-800">My worksheets</h2>
          <span className="text-xs text-slate-500">{past.length} total</span>
        </div>
        {past.length === 0 ? (
          <Card className="p-8 rounded-3xl border-2 border-dashed border-slate-300 bg-white text-center">
            <FileText className="mx-auto mb-3 text-slate-400" size={32} />
            <p className="text-slate-600 text-sm">
              No worksheets yet. Generate your first one above.
            </p>
          </Card>
        ) : (
          <div className="grid gap-3" data-testid="worksheets-past-list">
            {past.map((w) => (
              <motion.button
                key={w.id}
                onClick={() => navigate(`/worksheets/${w.id}`)}
                whileHover={{ x: 2 }}
                className="text-left bg-white border-2 border-slate-200 hover:border-emerald-300 rounded-2xl px-4 py-3 flex items-center justify-between transition-colors"
                data-testid={`worksheets-past-${w.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${w.graded_at ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {w.graded_at ? <CheckCircle2 size={18} /> : <Clock size={18} />}
                  </div>
                  <div>
                    <div className="font-bold text-slate-800 text-sm">
                      {w.type_label} · {w.level}
                    </div>
                    <div className="text-xs text-slate-500">
                      {new Date(w.created_at).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' })}
                      {w.graded_at && ` · Score: ${w.score}/${w.total || 10}`}
                    </div>
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-400" />
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorksheetsPage;
